/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';
import { graphql } from 'react-apollo';

import type { ApolloError } from 'apollo-client';

import useRouter from 'js/lib/useRouter';
import user from 'js/lib/user';

import { FullscreenDialog } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';
import { SvgLoaderSignal } from '@coursera/coursera-ui/svg';

import type {
  GetCoursesWithSessionsQuery_CoursesV1Resource_multiGet_elements as Course,
  GetCoursesWithSessionsQuery,
  GetCoursesWithSessionsQueryVariables,
} from 'bundles/degree-home/components/SessionJoinDialog/utils/__generated__/GetCoursesWithSessionsQuery';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/degree-home';

import EnrollmentConfirmationView from './EnrollmentConfirmationView';
import ErrorView from './ErrorView';
import NoEnrollmentRequiredView from './NoEnrollmentRequiredView';
import PickStartDateView from './PickStartDateView';
import type { EnrollingCourse } from './types/EnrollingCourse';
import { getCoursesWithSessionsQuery } from './utils/CoursesWithSessionsQueries';
import { enrollInMultipleCourses } from './utils/enrollInMultipleCourses';

type SessionJoinDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  // courseIds is used inside graphQL()
  courseIds: string[];
  courseIdsLoading?: boolean; // Allows getCoursesWithSessionsQuery to be delayed while courseIds are loading in a parent component
  onEnrolled?: () => void;
  enrollingCourseId?: string;
};

type PropsFromQuery = {
  enrollableCourses?: Course[] | null;
  enrollableCoursesLoading?: boolean;
  enrollableCoursesError?: ApolloError;
};

const styles = {
  courseraLogo: (theme: Theme) => css`
    width: 100px;
    margin-top: ${theme.spacing(8)};
  `,
  loadingIconWrapper: css`
    margin: 2rem 0;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
  `,
  noCoursesMessage: (theme: Theme) => css`
    padding: ${theme.spacing(32)} 0;
  `,
};

export const SessionJoinDialog: React.FunctionComponent<SessionJoinDialogProps & PropsFromQuery> = ({
  isOpen,
  onClose,
  onEnrolled,
  enrollingCourseId,
  enrollableCourses,
  enrollableCoursesLoading,
  enrollableCoursesError,
  courseIdsLoading,
}) => {
  const { Header, Content } = FullscreenDialog;
  const [successfulEnrollments, setSuccessfulEnrollments] = useState<Array<EnrollingCourse>>([]);
  const [enrollmentErrorMsg, setEnrollmentErrorMsg] = useState('');

  const router = useRouter();

  const handleClose = () => {
    onClose();
    setSuccessfulEnrollments([]);
    setEnrollmentErrorMsg('');
  };

  const handleEnroll = async (enrollingCourses: EnrollingCourse[]) => {
    try {
      const enrolledCourses = await enrollInMultipleCourses(enrollingCourses);
      setSuccessfulEnrollments(enrolledCourses);
    } catch (error) {
      setEnrollmentErrorMsg(_t('We can’t seem to enroll you into your selected courses right now, please try again.'));
    }

    if (onEnrolled) onEnrolled();
  };

  if (enrollableCoursesError || enrollmentErrorMsg) {
    return (
      <FullscreenDialog
        open={isOpen}
        onClose={handleClose}
        aria-describedby={_t('Session enrollment dialog')}
        contentWidth="60%"
        sticky="actions"
      >
        <Header />
        <TrackedDiv withVisibilityTracking trackingName="enrollment_dialog" />
        <ErrorView headingText={enrollmentErrorMsg && _t('Something went wrong')} bodyText={enrollmentErrorMsg} />
      </FullscreenDialog>
    );
  }

  const isLoading = courseIdsLoading || enrollableCoursesLoading;
  const hasEnrollableCourses = enrollableCourses && enrollableCourses.length > 0;
  const hasSuccesfullyEnrolled = successfulEnrollments.length > 0;
  /*
   * There are three situations here:
   * 1. enrollingCourse exists but it is already enrolled
   * 2. enrollingCourse does not exist, might caused by modify the course id in the url directly
   * 3. enrollingCourse exists and it is not enrolled yet
   
   */
  const enrollingCourse = enrollableCourses?.find((course: Course) => course.id === enrollingCourseId);
  const isEnrollingCourseEnrolled =
    enrollingCourseId && enrollingCourse?.activeAndUpcomingSessions?.elements.some((session) => session?.isEnrolled);

  /**
   * If situation 2 is met, change the url params from the incorrect course ID to simply showEnrollmentDialog=true
   */
  if (!isLoading && !enrollingCourse && enrollingCourseId) {
    const newQueryParams = { ...router.location.query };

    delete newQueryParams.enrollingCourseId;

    router.replace({
      ...router.location,
      params: router.params,
      query: { ...newQueryParams, showEnrollmentDialog: true },
    });
  }

  return (
    <FullscreenDialog
      open={isOpen}
      onClose={handleClose}
      aria-describedby={_t('Session enrollment dialog')}
      contentWidth="60%"
      sticky="actions"
    >
      <Header />

      <TrackedDiv withVisibilityTracking trackingName="enrollment_dialog" />
      {!hasSuccesfullyEnrolled && (
        <>
          {isLoading && (
            <Content aria-live="polite" aria-atomic="true">
              <div css={styles.loadingIconWrapper}>
                <SvgLoaderSignal title={_t('Loading.')} />
                <div>{_t('Loading sessions ...')}</div>
              </div>
            </Content>
          )}
          {!isLoading && !hasEnrollableCourses && (
            <NoEnrollmentRequiredView
              onClose={handleClose}
              headingText={_t('Nothing to do here! There are no courses available to enroll into right now.')}
              bodyText=""
            />
          )}
          {/* If situation 1 is met, display already enrolled view */}
          {!isLoading && isEnrollingCourseEnrolled && <NoEnrollmentRequiredView onClose={handleClose} />}
          {!isLoading && hasEnrollableCourses && !isEnrollingCourseEnrolled && (
            <PickStartDateView
              onEnroll={handleEnroll}
              onClose={handleClose}
              courses={enrollableCourses as Course[]}
              enrollingCourseId={enrollingCourseId}
            />
          )}
        </>
      )}

      {hasSuccesfullyEnrolled && (
        <>
          <EnrollmentConfirmationView enrollments={successfulEnrollments} onClose={handleClose} />
        </>
      )}
    </FullscreenDialog>
  );
};

export default graphql<
  SessionJoinDialogProps,
  GetCoursesWithSessionsQuery,
  GetCoursesWithSessionsQueryVariables,
  PropsFromQuery
>(getCoursesWithSessionsQuery, {
  options: ({ courseIds, enrollingCourseId }: SessionJoinDialogProps) => ({
    variables: {
      // add the enrollingCourseId to courseIds
      courseIds: enrollingCourseId ? Array.from(new Set([...courseIds, enrollingCourseId])) : courseIds,
      userId: user.get().id.toString(),
    },
  }),
  // We don't need to send this request unless there are enrollable courses specified
  skip: ({ courseIds, enrollingCourseId, courseIdsLoading }) =>
    courseIdsLoading || (!enrollingCourseId && courseIds.length < 1),
  props: ({ data }) => {
    const enrollableCourses = data?.CoursesV1Resource?.multiGet?.elements?.filter((course: Course | null) => {
      const sessions = course?.activeAndUpcomingSessions?.elements;
      return sessions && sessions.length > 0;
    }) as Course[];
    return {
      enrollableCoursesLoading: data?.loading as boolean,
      enrollableCoursesError: data?.error,
      enrollableCourses,
    };
  },
})(SessionJoinDialog);
